import { computed, onMounted, ref, watch } from 'vue'

const STORAGE_KEY = 'glamora-client-theme'

type ThemePreference = 'light' | 'dark' | 'system'
type ThemeResolved = 'light' | 'dark'

const preference = ref<ThemePreference>('system')
const resolved = ref<ThemeResolved>('light')

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function applyTheme(mode: ThemeResolved): void {
  const dark = mode === 'dark'
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light')
  document.documentElement.classList.toggle('dark', dark)
  resolved.value = dark ? 'dark' : 'light'
}

function resolve(pref: ThemePreference): ThemeResolved {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light'
  return pref === 'dark' ? 'dark' : 'light'
}

export function useTheme() {
  function setTheme(pref: ThemePreference): void {
    preference.value = pref
    try {
      localStorage.setItem(STORAGE_KEY, pref)
    } catch {
      /* ignore */
    }
    applyTheme(resolve(pref))
  }

  function cycleTheme(): void {
    const order: ThemePreference[] = ['light', 'dark', 'system']
    const i = order.indexOf(preference.value)
    setTheme(order[(i + 1) % order.length])
  }

  onMounted(() => {
    let saved: ThemePreference = 'system'
    try {
      saved = (localStorage.getItem(STORAGE_KEY) as ThemePreference) || 'system'
    } catch {
      /* ignore */
    }
    if (!['light', 'dark', 'system'].includes(saved)) saved = 'system'
    preference.value = saved
    applyTheme(resolve(saved))

    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (mq) {
      const onChange = () => {
        if (preference.value === 'system') applyTheme(resolve('system'))
      }
      mq.addEventListener?.('change', onChange)
    }
  })

  watch(preference, (p) => applyTheme(resolve(p)))

  return {
    preference,
    resolved,
    isDark: computed(() => resolved.value === 'dark'),
    setTheme,
    cycleTheme,
    options: [
      { label: 'Light', value: 'light', icon: 'lucide-sun' },
      { label: 'Dark', value: 'dark', icon: 'lucide-moon' },
      { label: 'System', value: 'system', icon: 'lucide-monitor' },
    ],
  }
}

/** Call once at app boot before paint if possible */
export function initThemeFromStorage(): void {
  try {
    const saved = (localStorage.getItem(STORAGE_KEY) as ThemePreference) || 'system'
    applyTheme(resolve(saved))
    preference.value = saved
  } catch {
    applyTheme('light')
  }
}